import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, ArrowRight, Tag, MagnifyingGlass, X } from '@phosphor-icons/react';
import axios from 'axios';
import { Seo } from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CommunityArticleView({ slug }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/community/${slug}`)
      .then(r => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <main className="pt-24 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto"></div>
      </div>
    </main>
  );

  if (!article) return (
    <main className="pt-24 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h1 className="font-outfit font-bold text-2xl text-neutral-900 mb-4">Article Not Found</h1>
        <p className="text-neutral-600 mb-6">The article you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/community" className="text-lime-600 font-medium hover:underline inline-flex items-center gap-2">
          <ArrowLeft size={18} /> Back to Community
        </Link>
      </div>
    </main>
  );

  return (
    <main className="pt-24 bg-white" data-testid="community-article-view">
      {article.image_url && (
        <section className="relative h-64 sm:h-80 lg:h-[28rem] overflow-hidden bg-neutral-100">
          <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/70 via-transparent to-transparent" />
        </section>
      )}
      <section className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/community" className="inline-flex items-center gap-2 text-lime-600 font-medium hover:text-lime-700 transition-colors mb-8" data-testid="community-back-link">
            <ArrowLeft size={18} weight="bold" /> Back to Community
          </Link>
          <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex flex-wrap items-center gap-4 text-neutral-500 text-sm mb-4">
              {article.category && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-lime-100 text-lime-700">
                  <Tag size={12} weight="duotone" />{article.category}
                </span>
              )}
              {article.date && (
                <div className="flex items-center gap-1.5">
                  <Calendar size={16} weight="duotone" className="text-lime-600" />
                  <span>{article.date}</span>
                </div>
              )}
              {article.author && (
                <>
                  <span className="text-neutral-300">|</span>
                  <div className="flex items-center gap-1.5">
                    <User size={16} weight="duotone" className="text-lime-600" />
                    <span>{article.author}</span>
                  </div>
                </>
              )}
            </div>
            <h1 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight mb-8" data-testid="community-article-title">
              {article.title}
            </h1>
            {article.excerpt && (
              <p className="text-lg text-neutral-600 leading-relaxed mb-8 border-l-4 border-lime-500 pl-5 italic">{article.excerpt}</p>
            )}
            {article.content ? (
              <div className="prose prose-neutral prose-lg max-w-none" data-testid="community-article-content">
                {article.content.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-neutral-700 leading-relaxed mb-5">{paragraph}</p>
                ))}
              </div>
            ) : (
              <p className="text-neutral-600 leading-relaxed">{article.excerpt}</p>
            )}
          </motion.article>
          <div className="mt-12 pt-8 border-t border-neutral-200">
            <div className="flex items-center justify-between">
              <Link to="/community" className="inline-flex items-center gap-2 text-lime-600 font-medium hover:text-lime-700 transition-colors">
                <ArrowLeft size={18} weight="bold" /> All Community
              </Link>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Get in Touch <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function CommunityCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden animate-pulse h-full">
      <div className="aspect-[16/10] bg-neutral-100" />
      <div className="p-6 space-y-3">
        <div className="h-3 w-1/3 bg-neutral-100 rounded" />
        <div className="h-5 w-4/5 bg-neutral-100 rounded" />
        <div className="h-4 w-full bg-neutral-100 rounded" />
        <div className="h-4 w-2/3 bg-neutral-100 rounded" />
      </div>
    </div>
  );
}

function CommunityGrid() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    axios.get(`${API}/community`)
      .then(r => setArticles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    articles.forEach(a => { if (a.category) set.add(a.category); });
    return Array.from(set).sort();
  }, [articles]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return articles.filter(a => {
      if (activeCategory !== 'all' && (a.category || '') !== activeCategory) return false;
      if (!q) return true;
      return `${a.title || ''} ${a.excerpt || ''} ${a.content || ''} ${a.author || ''} ${a.category || ''}`.toLowerCase().includes(q);
    });
  }, [articles, search, activeCategory]);

  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="community-grid">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Search + category filter */}
        <div className="mb-10 flex flex-col gap-4">
          <div className="relative max-w-xl">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" weight="bold" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search community stories..."
              className="w-full pl-10 pr-10 py-2.5 rounded-full bg-neutral-50 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-lime-500/40 focus:border-lime-400 text-neutral-800 placeholder-neutral-400 transition-all"
              data-testid="community-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 p-0.5 rounded-full"
                aria-label="Clear search"
                data-testid="community-search-clear"
              >
                <X size={14} weight="bold" />
              </button>
            )}
          </div>

          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="community-category-tabs">
              <button
                type="button"
                onClick={() => setActiveCategory('all')}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${activeCategory === 'all' ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-700 border-neutral-200 hover:border-lime-400 hover:text-lime-600'}`}
                data-testid="community-category-all"
              >
                All Types
              </button>
              {categories.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActiveCategory(c)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${activeCategory === c ? 'bg-lime-600 text-white border-lime-600' : 'bg-white text-neutral-700 border-neutral-200 hover:border-lime-400 hover:text-lime-600'}`}
                  data-testid={`community-category-${c.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          {!loading && (
            <p className="text-sm text-neutral-500">
              {filtered.length === articles.length ? `${articles.length} stories` : `${filtered.length} of ${articles.length} stories`}
              {activeCategory !== 'all' && ` in ${activeCategory}`}
              {search && ` matching "${search}"`}
            </p>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
            {[0, 1, 2].map(i => <CommunityCardSkeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-neutral-200 rounded-2xl">
            <p className="text-neutral-500 mb-2">No community stories match your filters.</p>
            {(search || activeCategory !== 'all') && (
              <button
                onClick={() => { setSearch(''); setActiveCategory('all'); }}
                className="text-lime-600 hover:text-lime-700 text-sm font-medium"
                data-testid="community-reset-filters"
              >
                Clear search &amp; filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr">
            {filtered.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.4) }}
                className="h-full"
              >
                <Link
                  to={`/community/${article.slug}`}
                  className="group flex flex-col h-full bg-white rounded-2xl border border-neutral-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-lime-200"
                  data-testid={`community-card-${article.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-neutral-100 shrink-0">
                    {article.image_url ? (
                      <img src={article.image_url} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-lime-50 to-neutral-100" />
                    )}
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {article.category && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-lime-100 text-lime-700">
                          <Tag size={11} weight="duotone" />{article.category}
                        </span>
                      )}
                      {article.date && <span className="text-xs text-neutral-500">{article.date}</span>}
                      {article.author && (
                        <>
                          <span className="text-xs text-neutral-300">|</span>
                          <span className="text-xs text-neutral-500">{article.author}</span>
                        </>
                      )}
                    </div>
                    <h3 className="font-outfit font-semibold text-lg text-neutral-900 group-hover:text-lime-600 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-neutral-600 text-sm line-clamp-3">{article.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-lime-600 text-sm font-medium mt-auto pt-4 group-hover:gap-2 transition-all">
                      Read More <ArrowRight size={14} weight="bold" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function CommunityPage() {
  const { slug } = useParams();

  if (slug) return <CommunityArticleView slug={slug} />;

  return (
    <main className="pt-24 bg-white" data-testid="community-page">
      <Seo title="Community" description="How NSEC engages with the local community through partnerships, sponsorships, education and environmental initiatives." path="/community" />
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="accent-font text-lime-600 mb-4">Community</p>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-neutral-900 tracking-tight mb-6">
              Our <span className="text-lime-600">Community</span>
            </h1>
            <p className="text-neutral-600 text-lg max-w-3xl">
              Discover how NSEC engages with the local community through partnerships,
              sponsorships, education programmes, and environmental initiatives.
            </p>
          </motion.div>
        </div>
      </section>
      <CommunityGrid />
    </main>
  );
}
